/**
 * observability-bridge.ts
 *
 * Bridge between @aaac/runtime execution and @aaac/observability agent-axis spans.
 *
 * Responsibilities:
 *  - Generate ExecutionEnvelope (run_id = UUIDv7) for every task/workflow execution
 *  - Emit agent.session (open/close) for runWorkflow() — root run span
 *  - Emit agent.subagent (open/close) for runTask() — handoff/task span
 *  - Emit toolchain.execution (open/close) for toolchain tool invocations
 *  - Observer is OPTIONAL — all emit* functions are no-ops when observer is undefined
 *
 * No circular dependency: @aaac/observability does NOT import @aaac/runtime.
 * Types (ExecutionEnvelope, EmitInput) are consumed from @aaac/observability.
 *
 * event-catalog.md §1.5, §2.2 / OBSERVABILITY_TASKS.md Phase 1 / architecture.md §6, §7
 */

import { generateId } from "@aaac/observability";
import type { ExecutionEnvelope, EmitInput, AttrValue, CanonicalLink } from "@aaac/observability";

// ── Re-exports ────────────────────────────────────────────────────────────────

export type { ExecutionEnvelope } from "@aaac/observability";

// ── TokenUsage ────────────────────────────────────────────────────────────────

/** Token usage metrics returned by LLM adapters after a query/session. */
export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens?: number;
  cacheReadTokens?: number;
  cacheCreationTokens?: number;
}

// ── Observability env propagation (git-hook correlation, #115) ─────────────────

/** Observability correlation ids propagated to spawned subprocesses. */
export interface ObservabilityIds {
  /** Agent-axis session id (= the session/run envelope run_id). */
  sessionId?: string;
  /** Distributed trace id, when available. */
  traceId?: string;
  /** Execution run id (ExecutionEnvelope.run_id). */
  runId?: string;
}

/** Environment variable names read by `.cursor/hooks/observ-record.sh`. */
export const OBSERVABILITY_ENV_VARS = {
  sessionId: "AAAC_SESSION_ID",
  traceId: "AAAC_TRACE_ID",
  runId: "AAAC_RUN_ID",
} as const;

/**
 * Build a subprocess environment that propagates the active observability
 * correlation ids as AAAC_SESSION_ID / AAAC_TRACE_ID / AAAC_RUN_ID.
 *
 * When @aaac/runtime spawns `git` (directly via a toolchain check, or
 * indirectly through an agent shell), the git `post-commit` hook runs
 * `.cursor/hooks/observ-record.sh`, whose highest-priority session_id source is
 * `$AAAC_SESSION_ID` (#115). Propagating these ids lets the promotion.commit /
 * promotion.file events emitted by the hook correlate to the originating agent
 * session.
 *
 * Returns a shallow clone of {@link baseEnv}; only ids that are defined and
 * non-empty are set, so the child never inherits empty/stale values.
 */
export function buildObservabilityEnv(
  ids: ObservabilityIds,
  baseEnv: NodeJS.ProcessEnv = process.env,
): NodeJS.ProcessEnv {
  const env: NodeJS.ProcessEnv = { ...baseEnv };
  if (ids.sessionId) env[OBSERVABILITY_ENV_VARS.sessionId] = ids.sessionId;
  if (ids.traceId) env[OBSERVABILITY_ENV_VARS.traceId] = ids.traceId;
  if (ids.runId) env[OBSERVABILITY_ENV_VARS.runId] = ids.runId;
  return env;
}

// ── ObserverEmitFn ────────────────────────────────────────────────────────────

/**
 * Minimal observer emit signature — structurally matches EventCollector.emit().
 * Pass `collector.emit.bind(collector)` to wire up the full observability pipeline.
 * When undefined, all emission is a no-op.
 */
export type ObserverEmitFn = (event: EmitInput) => void;

/**
 * Serialize writes through a shared in-process observer.
 * Concurrent workflow steps that emit in parallel can hit SQLITE_BUSY on the
 * shared SqliteSink without this guard.
 */
export function serializeObserverEmit(emit: ObserverEmitFn): ObserverEmitFn {
  let queue: EmitInput[] = [];
  let draining = false;

  function drain(): void {
    if (draining) return;
    draining = true;
    try {
      while (queue.length > 0) {
        const next = queue.shift()!;
        emit(next);
      }
    } finally {
      draining = false;
      if (queue.length > 0) {
        drain();
      }
    }
  }

  return (event: EmitInput) => {
    queue.push(event);
    drain();
  };
}

// ── Envelope creation ─────────────────────────────────────────────────────────

export interface EnvelopeParams {
  /** run_id of the parent execution (populated by workflow-runner for task spans). */
  parentRunId?: string;
  /** Component / agent identifier from the agent-contracts definition. */
  componentId?: string;
  /** Agent that issued this execution (handoff source). */
  fromAgent?: string;
  /** Agent that receives this execution (handoff target). */
  toAgent?: string;
  /** agent-contracts schema version. */
  contractVersion?: string;
  /** LLM adapter identifier (e.g. 'claude-agent-sdk'). */
  adapter?: string;
  /** LLM model identifier (e.g. 'claude-opus-4-5'). */
  model?: string;
  /** Task instance identifier within the run. */
  taskInstanceId?: string;
}

/**
 * Create an ExecutionEnvelope with a fresh UUIDv7 run_id and current timestamp.
 * Only defined fields are included — undefined values are omitted.
 */
export function createExecutionEnvelope(params: EnvelopeParams = {}): ExecutionEnvelope {
  const envelope: ExecutionEnvelope = {
    run_id: generateId(),
    timestamp: new Date().toISOString(),
  };
  if (params.parentRunId !== undefined) envelope.parent_run_id = params.parentRunId;
  if (params.componentId !== undefined) envelope.component_id = params.componentId;
  if (params.fromAgent !== undefined) envelope.from_agent = params.fromAgent;
  if (params.toAgent !== undefined) envelope.to_agent = params.toAgent;
  if (params.contractVersion !== undefined) envelope.contract_version = params.contractVersion;
  if (params.adapter !== undefined) envelope.adapter = params.adapter;
  if (params.model !== undefined) envelope.model = params.model;
  if (params.taskInstanceId !== undefined) envelope.task_instance_id = params.taskInstanceId;
  return envelope;
}

// ── attribute builder helpers ─────────────────────────────────────────────────

/** Build attribute record; omits keys with undefined/empty-string values. */
function attrs(base: Record<string, AttrValue | undefined>): Record<string, AttrValue> {
  const out: Record<string, AttrValue> = {};
  for (const [k, v] of Object.entries(base)) {
    if (v !== undefined && v !== "") out[k] = v;
  }
  return out;
}

/** Resolve agent.session_id for Agent-axis child spans. */
function resolveAgentSessionId(envelope: ExecutionEnvelope, parentSpanId?: string): string {
  return envelope.parent_run_id ?? parentSpanId ?? envelope.run_id;
}

/**
 * Extract token usage fields into OTel GenAI semantic conventions attributes.
 *
 * Standard keys (#125 §6):
 *   gen_ai.usage.input_tokens  — OTel GenAI: total input tokens for the operation
 *   gen_ai.usage.output_tokens — OTel GenAI: total output tokens for the operation
 *   gen_ai.usage.total_tokens  — non-standard extension (no OTel equivalent)
 *   gen_ai.usage.cache_read_input_tokens    — Anthropic-specific extension
 *   gen_ai.usage.cache_creation_input_tokens — Anthropic-specific extension
 *   cost_usd — OTel GenAI に標準キーが存在しないためカスタム属性のまま据え置き
 */
function tokenAttrs(meta: Record<string, unknown>): Record<string, AttrValue> {
  const out: Record<string, AttrValue> = {};
  const tu = meta.token_usage;
  if (tu && typeof tu === "object") {
    const t = tu as Record<string, unknown>;
    if (typeof t.input_tokens === "number") out["gen_ai.usage.input_tokens"] = t.input_tokens;
    if (typeof t.output_tokens === "number") out["gen_ai.usage.output_tokens"] = t.output_tokens;
    if (typeof t.total_tokens === "number") out["gen_ai.usage.total_tokens"] = t.total_tokens;
    if (typeof t.cache_read_input_tokens === "number") out["gen_ai.usage.cache_read_input_tokens"] = t.cache_read_input_tokens;
    if (typeof t.cache_creation_input_tokens === "number") out["gen_ai.usage.cache_creation_input_tokens"] = t.cache_creation_input_tokens;
  }
  if (typeof meta.cost_usd === "number") out["cost_usd"] = meta.cost_usd;
  return out;
}

// ── agent.session ─────────────────────────────────────────────────────────────

/**
 * Emit an agent.session open span (root workflow/session execution).
 *
 * Uses envelope.run_id as the spanId so that child agent.subagent spans can
 * reference it as parentSpanId for hierarchical correlation.
 *
 * Returns the spanId emitted (= envelope.run_id).
 * Returns undefined when observer is undefined (no-op).
 *
 * event-catalog.md §2.2 — required: agent.session_id; axis: agent
 */
export function emitSessionOpen(
  observer: ObserverEmitFn | undefined,
  envelope: ExecutionEnvelope,
  workflowId: string,
): string | undefined {
  if (!observer) return undefined;

  const spanId = envelope.run_id;
  observer({
    source: "aaac-runtime",
    eventType: "agent.session",
    lifecycle: "open",
    spanId,
    parentSpanId: envelope.parent_run_id,
    attributes: attrs({
      axis: "agent",
      "agent.session_id": envelope.run_id,
      "agent.run_id": envelope.run_id,
      "workflow.id": workflowId,
      "component.id": envelope.component_id,
      "gen_ai.request.model": envelope.model,
      "gen_ai.system": envelope.adapter,
      contract_version: envelope.contract_version,
    }),
  });
  return spanId;
}

/**
 * Emit an agent.session close span.
 *
 * `errorMessage` is set on the exception path (workflow body threw) so the
 * failure cause is recorded alongside the run status. Omitted on the normal path.
 *
 * event-catalog.md §2.2 — optional: agent.session.duration_ms, agent.status, agent.error
 */
export function emitSessionClose(
  observer: ObserverEmitFn | undefined,
  spanId: string,
  envelope: ExecutionEnvelope,
  durationMs: number,
  status: string,
  errorMessage?: string,
): void {
  if (!observer) return;

  const metaAttrs: Record<string, AttrValue> =
    envelope.execution_metadata ? tokenAttrs(envelope.execution_metadata) : {};

  observer({
    source: "aaac-runtime",
    eventType: "agent.session",
    lifecycle: "close",
    spanId,
    attributes: {
      ...attrs({
        axis: "agent",
        "agent.session_id": envelope.run_id,
        "agent.session.duration_ms": durationMs,
        "agent.status": status,
        "agent.error": errorMessage,
      }),
      ...metaAttrs,
    },
  });
}

// ── agent.subagent ────────────────────────────────────────────────────────────

/**
 * Emit an agent.subagent open span (task/handoff execution).
 *
 * Uses envelope.run_id as the spanId and parentSpanId for hierarchical correlation
 * (typically the session span's run_id).
 *
 * Returns the spanId emitted (= envelope.run_id).
 * Returns undefined when observer is undefined (no-op).
 *
 * event-catalog.md §2.2 — required: agent.role; axis: agent
 */
export function emitSubagentOpen(
  observer: ObserverEmitFn | undefined,
  envelope: ExecutionEnvelope,
  parentSpanId: string | undefined,
  taskId: string,
  agentRole: string,
  sessionId?: string,
): string | undefined {
  if (!observer) return undefined;

  const spanId = envelope.run_id;
  observer({
    source: "aaac-runtime",
    eventType: "agent.subagent",
    lifecycle: "open",
    spanId,
    parentSpanId,
    attributes: attrs({
      axis: "agent",
      "agent.session_id": sessionId ?? resolveAgentSessionId(envelope, parentSpanId),
      "agent.role": agentRole,
      "agent.kind": "task",
      "task.id": taskId,
      "agent.run_id": envelope.run_id,
      "agent.parent_run_id": envelope.parent_run_id,
      "gen_ai.request.model": envelope.model,
      "gen_ai.system": envelope.adapter,
      "component.id": envelope.component_id,
      "agent.from_agent": envelope.from_agent,
      "agent.to_agent": envelope.to_agent,
    }),
  });
  return spanId;
}

/**
 * Emit an agent.subagent close span.
 *
 * `errorMessage` records the failure cause on `agent.error` (parity with
 * emitSessionClose); it is omitted from the attributes when undefined.
 *
 * event-catalog.md §2.2 — optional: agent.duration_ms, agent.status, agent.error,
 * token_usage, cost_usd
 */
export function emitSubagentClose(
  observer: ObserverEmitFn | undefined,
  spanId: string,
  envelope: ExecutionEnvelope,
  durationMs: number,
  status: string,
  agentRole: string,
  errorMessage?: string,
  sessionId?: string,
): void {
  if (!observer) return;

  const metaAttrs: Record<string, AttrValue> =
    envelope.execution_metadata ? tokenAttrs(envelope.execution_metadata) : {};

  observer({
    source: "aaac-runtime",
    eventType: "agent.subagent",
    lifecycle: "close",
    spanId,
    attributes: {
      ...attrs({
        axis: "agent",
        "agent.session_id": sessionId ?? resolveAgentSessionId(envelope),
        "agent.role": agentRole,
        "agent.status": status,
        "agent.duration_ms": durationMs,
        "agent.error": errorMessage,
      }),
      ...metaAttrs,
    },
  });
}

// ── evaluate.iteration ────────────────────────────────────────────────────────

/**
 * Emit an evaluate.iteration open span (one closed-loop iteration).
 *
 * Returns the spanId emitted (= generated UUIDv7).
 * Returns undefined when observer is undefined (no-op).
 */
export function emitIterationOpen(
  observer: ObserverEmitFn | undefined,
  parentSpanId: string | undefined,
  evaluateTaskId: string,
  loopTo: string,
  iterationNumber: number,
  maxIterations: number,
): string | undefined {
  if (!observer) return undefined;

  const spanId = generateId();
  observer({
    source: "aaac-runtime",
    eventType: "evaluate.iteration",
    lifecycle: "open",
    spanId,
    parentSpanId,
    attributes: attrs({
      axis: "agent",
      "evaluate.task": evaluateTaskId,
      "evaluate.loop_to": loopTo,
      "evaluate.iteration": iterationNumber,
      "evaluate.max_iterations": maxIterations,
    }),
  });
  return spanId;
}

/**
 * Emit an evaluate.iteration close span.
 */
export function emitIterationClose(
  observer: ObserverEmitFn | undefined,
  spanId: string,
  evaluateTaskId: string,
  iterationNumber: number,
  verdict: string,
  durationMs: number,
  feedbackInjected: boolean,
): void {
  if (!observer) return;

  observer({
    source: "aaac-runtime",
    eventType: "evaluate.iteration",
    lifecycle: "close",
    spanId,
    attributes: attrs({
      axis: "agent",
      "evaluate.task": evaluateTaskId,
      "evaluate.iteration": iterationNumber,
      "evaluate.verdict": verdict,
      "evaluate.duration_ms": durationMs,
      "evaluate.feedback_injected": feedbackInjected,
    }),
  });
}

/**
 * Emit an evaluate.metric instant event when an evaluate step terminates.
 */
export function emitIterationMetric(
  observer: ObserverEmitFn | undefined,
  parentSpanId: string | undefined,
  evaluateTaskId: string,
  totalIterations: number,
  terminalOutcome: string,
): void {
  if (!observer) return;

  const spanId = generateId();
  observer({
    source: "aaac-runtime",
    eventType: "evaluate.metric",
    lifecycle: "instant",
    spanId,
    parentSpanId,
    attributes: attrs({
      axis: "agent",
      "evaluate.task": evaluateTaskId,
      "evaluate.total_iterations": totalIterations,
      "evaluate.terminal_outcome": terminalOutcome,
    }),
  });
}

// ── InnerSpanOptions (for adapter plumbing) ──────────────────────────────────

/** Options forwarded from task-runner to adapters for inner turn-level spans. */
export interface InnerSpanOptions {
  observer?: ObserverEmitFn;
  parentSpanId?: string;
  /**
   * Agent-axis session id (= the enclosing agent.session run_id). Threaded so
   * inner agent.llm_turn / agent.tool_exec spans carry a non-NULL session_id and
   * join the cross-axis correlation rules (#143 / #140).
   */
  sessionId?: string;
}

// ── agent.llm_turn ───────────────────────────────────────────────────────────

/**
 * Emit an agent.llm_turn open span when a new LLM turn begins.
 *
 * Returns the spanId emitted (= generated UUIDv7).
 * Returns undefined when observer is undefined (no-op).
 */
export function emitLlmTurnOpen(
  observer: ObserverEmitFn | undefined,
  parentSpanId: string | undefined,
  turnNumber: number,
  model?: string,
  llmParams?: { temperature?: number; reasoningEffort?: string },
  sessionId?: string,
): string | undefined {
  if (!observer) return undefined;

  const spanId = generateId();
  observer({
    source: "aaac-runtime",
    eventType: "agent.llm_turn",
    lifecycle: "open",
    spanId,
    parentSpanId,
    attributes: attrs({
      axis: "agent",
      "agent.session_id": sessionId,
      "llm.turn_number": turnNumber,
      "gen_ai.request.model": model,
      "llm.temperature": llmParams?.temperature,
      "llm.reasoning_effort": llmParams?.reasoningEffort,
    }),
  });
  return spanId;
}

/**
 * Emit an agent.llm_turn close span.
 */
export function emitLlmTurnClose(
  observer: ObserverEmitFn | undefined,
  spanId: string,
  turnNumber: number,
  durationMs: number,
  tokenAttrs?: {
    inputTokens?: number;
    outputTokens?: number;
    cacheHitTokens?: number;
    toolCallsCount?: number;
  },
  sessionId?: string,
): void {
  if (!observer) return;

  observer({
    source: "aaac-runtime",
    eventType: "agent.llm_turn",
    lifecycle: "close",
    spanId,
    attributes: attrs({
      axis: "agent",
      "agent.session_id": sessionId,
      "llm.turn_number": turnNumber,
      "llm.duration_ms": durationMs,
      "gen_ai.usage.input_tokens": tokenAttrs?.inputTokens,
      "gen_ai.usage.output_tokens": tokenAttrs?.outputTokens,
      "llm.cache_hit_tokens": tokenAttrs?.cacheHitTokens,
      "llm.tool_calls_count": tokenAttrs?.toolCallsCount,
    }),
  });
}

// ── agent.tool_exec ──────────────────────────────────────────────────────────

/** Optional emit-time attributes and links for agent.tool_exec spans (#151). */
export interface ToolExecOpenOptions {
  sessionId?: string;
  extraAttributes?: Record<string, AttrValue>;
  links?: CanonicalLink[];
}

export interface ToolExecCloseOptions {
  sessionId?: string;
  extraAttributes?: Record<string, AttrValue>;
  outputSummary?: string;
}

function resolveToolExecOpenOptions(
  sessionIdOrOptions?: string | ToolExecOpenOptions,
): ToolExecOpenOptions {
  if (typeof sessionIdOrOptions === "string") {
    return { sessionId: sessionIdOrOptions };
  }
  return sessionIdOrOptions ?? {};
}

function resolveToolExecCloseOptions(
  sessionIdOrOptions?: string | ToolExecCloseOptions,
): ToolExecCloseOptions {
  if (typeof sessionIdOrOptions === "string") {
    return { sessionId: sessionIdOrOptions };
  }
  return sessionIdOrOptions ?? {};
}

/**
 * Emit an agent.tool_exec open span when a tool execution begins.
 *
 * Returns the spanId emitted (= generated UUIDv7).
 * Returns undefined when observer is undefined (no-op).
 */
export function emitToolExecOpen(
  observer: ObserverEmitFn | undefined,
  parentSpanId: string | undefined,
  toolName: string,
  inputSummary: string,
  turnNumber: number,
  sessionIdOrOptions?: string | ToolExecOpenOptions,
): string | undefined {
  if (!observer) return undefined;

  const opts = resolveToolExecOpenOptions(sessionIdOrOptions);
  const spanId = generateId();
  observer({
    source: "aaac-runtime",
    eventType: "agent.tool_exec",
    lifecycle: "open",
    spanId,
    parentSpanId,
    links: opts.links,
    attributes: {
      ...attrs({
        axis: "agent",
        "agent.session_id": opts.sessionId,
        "tool.name": toolName,
        "tool.input_summary": inputSummary,
        "llm.turn_number": turnNumber,
      }),
      ...(opts.extraAttributes ?? {}),
    },
  });
  return spanId;
}

/**
 * Emit an agent.tool_exec close span.
 */
export function emitToolExecClose(
  observer: ObserverEmitFn | undefined,
  spanId: string,
  toolName: string,
  durationMs: number,
  sessionIdOrOptions?: string | ToolExecCloseOptions,
): void {
  if (!observer) return;

  const opts = resolveToolExecCloseOptions(sessionIdOrOptions);
  observer({
    source: "aaac-runtime",
    eventType: "agent.tool_exec",
    lifecycle: "close",
    spanId,
    attributes: {
      ...attrs({
        axis: "agent",
        "agent.session_id": opts.sessionId,
        "tool.name": toolName,
        "tool.duration_ms": durationMs,
        "tool.output_summary": opts.outputSummary,
      }),
      ...(opts.extraAttributes ?? {}),
    },
  });
}

// ── toolchain.execution ──────────────────────────────────────────────────────

/**
 * Emit a toolchain.execution open span when a toolchain tool is invoked.
 *
 * Returns the spanId emitted (= generated UUIDv7).
 * Returns undefined when observer is undefined (no-op).
 */
export function emitToolchainOpen(
  observer: ObserverEmitFn | undefined,
  parentSpanId: string | undefined,
  toolchainId: string,
  operation: string,
  artifactIds: string[],
  componentId?: string,
): string | undefined {
  if (!observer) return undefined;

  const spanId = generateId();
  observer({
    source: "aaac-runtime",
    eventType: "toolchain.execution",
    lifecycle: "open",
    spanId,
    parentSpanId,
    attributes: attrs({
      axis: "toolchain",
      "toolchain.id": toolchainId,
      "toolchain.operation": operation,
      "toolchain.artifact_ids": artifactIds.join(","),
      "component.id": componentId,
    }),
  });
  return spanId;
}

/**
 * Emit a toolchain.execution close span.
 */
export function emitToolchainClose(
  observer: ObserverEmitFn | undefined,
  spanId: string,
  toolchainId: string,
  operation: string,
  durationMs: number,
  status: "success" | "failure",
  errorMessage?: string,
): void {
  if (!observer) return;

  observer({
    source: "aaac-runtime",
    eventType: "toolchain.execution",
    lifecycle: "close",
    spanId,
    attributes: attrs({
      axis: "toolchain",
      "toolchain.id": toolchainId,
      "toolchain.operation": operation,
      "toolchain.status": status,
      "toolchain.duration_ms": durationMs,
      ...(errorMessage ? { "toolchain.error": errorMessage } : {}),
    }),
  });
}
